import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Popover } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useAccountNavigationStore } from "@companion-core/shared/app/Store/accountNavigationStore";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { useMemberStore } from "@companion-core/shared/app/Store/memberStore";
import { WizardStoreProvider } from "@companion-core/shared/app/Store/wizardStore";
import {
  NavigationItemObject,
  NavigationListObject,
} from "@companion-core/shared/app/Interfaces/navigation";
import { NavigationList } from "@companion-core/shared/app/Components/NavigationList";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import {
  LanguageSelector,
  PersonalInformation,
} from "@companion-core/shared/app/Components/MyAccount/Sections";
import { Preferences } from "@companion-core/shared/app/Components/MyAccount/Sections/Preferences";
import { PopIn } from "@companion-core/shared/app/Components/PopIn";
import { Security } from "@companion-core/shared/app/Components/MyAccount/Sections/Security";
import { getNavigationListsBasedOnAuthenticationStatus } from "@companion-core/shared/app/Utils/config";
import { isDarkColor } from "@companion-core/shared/app/Utils/colors";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { lockScroll, unlockScroll } from "Utilities/scroll";
import "Assets/Styles/scss/myAccount.scss";

const navigationContentMap: { [key: string]: ReactElement } = {
  "personal-information": <PersonalInformation />,
  "security": <Security />,
  "preferences": <Preferences />,
  language: <LanguageSelector />,
};

interface AccountMobileContentProps {
  navigationContentName: string | null;
  accountNavigationList: ReactElement;
  isLogoutPopInOpen: boolean;
  onLogoutClicked: () => void;
  setIsLogoutPopInOpen: (value: boolean) => void;
  t: (value: string) => string;
}

const AccountMobileContent = ({
  navigationContentName,
  accountNavigationList,
  isLogoutPopInOpen,
  onLogoutClicked,
  setIsLogoutPopInOpen,
  t,
}: AccountMobileContentProps) => {
  return (
    <div data-testid="account-popover__content" className="account-popover__content">
      {navigationContentName ? (
        <WizardStoreProvider>{navigationContentMap[navigationContentName]}</WizardStoreProvider>
      ) : (
        accountNavigationList
      )}
      <PopIn
        isOpen={isLogoutPopInOpen}
        testID="account-popover__logout-pop-in"
        content={<span className="body-text-6">{t("common.logout.pop-in.content")}</span>}
        primaryButton={{
          text: t("common.logout.pop-in.confirm.label"),
          onClick: () => onLogoutClicked(),
        }}
        secondaryButton={{
          text: t("common.logout.pop-in.cancel.label"),
          onClick: () => setIsLogoutPopInOpen(false),
        }}
        onClose={() => setIsLogoutPopInOpen(false)}
      />
    </div>
  );
};

export const AccountPopover = () => {
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentNavigationLists, setCurrentNavigationLists] = useState<NavigationListObject[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLogoutPopInOpen, setIsLogoutPopInOpen] = useState<boolean>(false);
  const { authenticated, logout } = useAuthStore((state) => ({
    authenticated: state.authenticated,
    logout: state.logout,
  }));
  const { resetMember, resetValidationBanner } = useMemberStore((state) => ({
    resetMember: state.resetMember,
    resetValidationBanner: state.resetValidationBanner,
  }));
  const { config, branding } = useConfigStore((state) => ({
    config: state.config,
    branding: state.branding,
  }));
  const { isIntroPopUpVisible, setIsIntroPopUpVisible } = useBellStore();
  const {
    isPopoverOpen,
    isBackButtonOnHomeVisible,
    navigationTree,
    navigationContentName,
    closePopover,
    addListsToNavigationTree,
    deleteLastListsFromNavigationTree,
    setNavigationContentName,
    resetAccountNavigation,
  } = useAccountNavigationStore();
  const navigationLists: NavigationListObject[] = useMemo(
    () => getNavigationListsBasedOnAuthenticationStatus(config, authenticated) ?? [],
    [config, authenticated]
  );
  const logoUri = useMemo(() => branding?.theme?.assets?.logo?.default, [branding]);
  const isHeaderBackgroundDark = useMemo(() => {
    const headerBackground = branding?.theme?.colors?.["color-header-background-primary"];
    if (headerBackground) {
      return isDarkColor(headerBackground);
    }
  }, [branding]);

  useEffect(() => {
    if (isPopoverOpen) {
      if (!queryClient.isFetching({ queryKey: ["member"] })) {
        queryClient.invalidateQueries({ queryKey: ["member"] });
      }
      if (!navigationTree.length) {
        addListsToNavigationTree(navigationLists);
      }
      isIntroPopUpVisible && setIsIntroPopUpVisible(false);
      lockScroll();
    }
    !isPopoverOpen && unlockScroll();
    setIsLoaded(true);
  }, [isPopoverOpen]);

  useEffect(() => {
    if (navigationTree.length) {
      setCurrentNavigationLists(navigationTree[navigationTree.length - 1]);
    } else {
      setCurrentNavigationLists([]);
    }
  }, [navigationTree]);

  useEffect(() => {
    if (!isMobileSize && isLoaded) {
      cleanAndClosePopover();
    }
  }, [isMobileSize]);

  const onLogoutClicked = () => {
    setIsLogoutPopInOpen(false);
    logout();
    resetMember();
    cleanAndClosePopover();
    navigate("login");
  };

  const cleanAndClosePopover = () => {
    resetAccountNavigation();
    closePopover();
  };

  const showBackButton = useMemo(
    () =>
      isBackButtonOnHomeVisible ||
      (!isBackButtonOnHomeVisible && (navigationTree.length > 1 || navigationContentName)),
    [isBackButtonOnHomeVisible, navigationTree, navigationContentName]
  );

  const handleBackButton = () => {
    if (navigationTree.length <= 1 && !navigationContentName) {
      cleanAndClosePopover();
    } else if (navigationContentName) {
      setNavigationContentName(null);
    } else {
      deleteLastListsFromNavigationTree();
    }
  };

  const AccountMobileHeader = () => (
    <div
      data-testid="account-popover__title"
      className={`account-popover__title ${isHeaderBackgroundDark ? "account-popover__title--negative" : ""}`}>
      {showBackButton && (
        <FontIcon
          data-testid="account-popover__back"
          name="JP-Arrow-left-bold"
          size={24}
          onClick={handleBackButton}
        />
      )}
      <div className="account-popover__logo">
        <img
          data-testid="account-popover__logo"
          src={logoUri}
          alt="logo"
          onClick={() => {
            cleanAndClosePopover();
            navigate("/");
          }}
        />
      </div>
    </div>
  );

  const AccountNavigationList = () => {
    return (
      <>
        {currentNavigationLists.map((navigationList: NavigationListObject) => (
          <NavigationList
            key={navigationList.name}
            name={navigationList.name}
            displayTitle={navigationList?.displayTitle}
            navigationItems={navigationList.navigationItems}
            onEnterInternalNavigation={(navigationItem: NavigationItemObject) => {
              if (navigationItem.path) {
                cleanAndClosePopover();
                navigate(navigationItem.path);
              }
            }}
            onEnterNestedNavigation={(navigationItem: NavigationItemObject) => {
              if (navigationItem.nestedNavigationLists) {
                addListsToNavigationTree(navigationItem.nestedNavigationLists);
              } else {
                resetValidationBanner();
                setNavigationContentName(navigationItem.name);
              }
            }}
            onEnterLogoutNavigation={() => setIsLogoutPopInOpen(true)}
          />
        ))}
      </>
    );
  };

  return (
    <Popover
      title={<AccountMobileHeader />}
      content={
        <AccountMobileContent
          accountNavigationList={<AccountNavigationList />}
          isLogoutPopInOpen={isLogoutPopInOpen}
          navigationContentName={navigationContentName}
          onLogoutClicked={onLogoutClicked}
          setIsLogoutPopInOpen={setIsLogoutPopInOpen}
          t={t}
        />
      }
      trigger="click"
      arrow={false}
      autoAdjustOverflow={false}
      open={isPopoverOpen}
      classNames={{ root: "account-popover" }}
    />
  );
};
